import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Mic, Headphones, Play, Shield, BarChart, Settings2, ArrowRight } from 'lucide-react';

const VoiceCoach: React.FC = () => {
    const navigate = useNavigate();

    const handleUpgrade = () => {
        navigate('/signup');
    };

    return (
        <div className="bg-slate-50 min-h-screen pt-32 pb-20 font-sans">
            {/* Header Section */}
            <div className="bg-[#011E26] text-white py-12 mb-16 border-b border-[#009B8F]/30">
                <div className="max-w-7xl mx-auto px-6 flex flex-col md:flex-row justify-between items-center gap-8">
                    <div>
                        <div className="flex items-center gap-3 mb-4">
                            <span className="w-3 h-3 bg-[#009B8F] rounded-full animate-pulse"></span>
                            <span className="text-[#009B8F] font-bold tracking-widest text-xs uppercase">Live AI Assistant</span>
                        </div>
                        <h1 className="text-4xl md:text-6xl font-bold mb-4">AI Voice <span className="text-[#009B8F]">Coach</span></h1>
                        <p className="text-slate-400 text-lg md:text-xl max-w-xl">
                            Master your spoken English with your always-available Audio AI companion.
                        </p>
                    </div>
                    <div className="bg-white/5 backdrop-blur-xl p-8 rounded-3xl border border-white/10 shadow-2xl flex items-center gap-6">
                        <div className="w-20 h-20 bg-[#009B8F] rounded-full flex items-center justify-center shadow-[0_0_30px_rgba(0,155,143,0.5)]">
                            <Mic className="w-10 h-10 text-[#011E26]" />
                        </div>
                        <div className="space-y-1">
                            <p className="text-xs font-bold text-slate-400 uppercase tracking-widest">Acoustics Active</p>
                            <p className="text-xl font-bold">Ready to Practice</p>
                        </div>
                    </div>
                </div>
            </div>

            <div className="max-w-7xl mx-auto px-6">
                {/* Main Value Proposition */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-20 items-center mb-32">
                    <div className="order-2 lg:order-1">
                        <div className="relative group">
                            <div className="absolute -inset-1 bg-gradient-to-r from-[#009B8F] to-[#011E26] rounded-3xl blur opacity-25 group-hover:opacity-100 transition duration-1000 group-hover:duration-200"></div>
                            <img
                                src="https://images.unsplash.com/photo-1543269865-cbf427effbad?q=80&w=2070&auto=format&fit=crop"
                                alt="Student practicing speaking"
                                className="relative rounded-3xl shadow-2xl hover:scale-[1.02] transition-transform duration-500"
                            />
                        </div>
                    </div>
                    <div className="order-1 lg:order-2 space-y-8">
                        <h2 className="text-3xl md:text-4xl font-bold text-[#011E26] leading-tight">
                            A Private, <span className="text-[#009B8F]">Judgment-Free</span> Environment.
                        </h2>
                        <div className="space-y-6 text-slate-600 text-lg">
                            <p>
                                Speaking is often the most intimidating part of learning English, especially when practicing in
                                front of others. The AI Voice Coach gives you a private space to speak freely.
                            </p>
                            <p>
                                Instead of relying on expensive tutors or limited schedules, you get an on-demand speaking
                                partner powered by advanced acoustic analysis.
                            </p>
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                            <div className="p-6 bg-white rounded-2xl border border-slate-200 shadow-sm">
                                <Shield className="w-8 h-8 text-[#009B8F] mb-3" />
                                <h4 className="font-bold text-[#011E26]">Private Sessions</h4>
                                <p className="text-xs text-slate-500 uppercase tracking-wider mt-1">100% Confidential</p>
                            </div>
                            <div className="p-6 bg-white rounded-2xl border border-slate-200 shadow-sm">
                                <Headphones className="w-8 h-8 text-[#009B8F] mb-3" />
                                <h4 className="font-bold text-[#011E26]">HD Audio Analysis</h4>
                                <p className="text-xs text-slate-500 uppercase tracking-wider mt-1">Real-time feedback</p>
                            </div>
                        </div>
                    </div>
                </div>

                {/* The Feedback Loop Section */}
                <div className="bg-white rounded-[3rem] p-12 md:p-24 shadow-2xl mb-32 relative overflow-hidden border border-slate-100">
                    <div className="max-w-4xl mx-auto">
                        <div className="text-center mb-16">
                            <h2 className="text-4xl font-bold text-[#011E26] mb-6">A Smarter Way to Improve</h2>
                            <p className="text-slate-500 text-lg">Our refined feedback loop helps you master the qualities examiners look for.</p>
                        </div>

                        <div className="space-y-12">
                            {[
                                {
                                    step: "01",
                                    title: "Natural Scenarios",
                                    desc: "Practise with interactive speaking scenarios that simulate real exam conditions and workplace environments."
                                },
                                {
                                    step: "02",
                                    title: "Instant Transcription",
                                    desc: "Every practice session is automatically transcribed and analysed for grammar, vocabulary and fluency."
                                },
                                {
                                    step: "03",
                                    title: "Acoustic Mapping",
                                    desc: "The system identifies unclear sounds, unnatural pauses, and rhythm patterns that affect your delivery."
                                }
                            ].map((item, i) => (
                                <div key={i} className="flex gap-8 items-start group">
                                    <span className="text-6xl font-black text-slate-100 group-hover:text-[#009B8F]/20 transition-colors leading-none">{item.step}</span>
                                    <div>
                                        <h3 className="text-2xl font-bold text-[#011E26] mb-3 group-hover:text-[#009B8F] transition-colors">{item.title}</h3>
                                        <p className="text-slate-600 text-lg leading-relaxed">{item.desc}</p>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>

                {/* Insights & Analytics */}
                <div className="mb-32">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
                        <div className="bg-[#009B8F] rounded-[3rem] p-12 text-white shadow-2xl">
                            <BarChart className="w-12 h-12 mb-8 text-[#011E26]" />
                            <h3 className="text-3xl font-bold mb-6">Master Your Progress</h3>
                            <p className="text-white/80 text-lg mb-8">
                                Every session is saved to your personalised dashboard. Visualize your improvement in
                                pronunciation, vocabulary richness, and overall confidence scores over time.
                            </p>
                            <div className="h-2 w-full bg-[#011E26]/20 rounded-full overflow-hidden">
                                <div className="h-full bg-white w-3/4 animate-pulse"></div>
                            </div>
                            <p className="mt-4 text-xs font-bold uppercase tracking-widest opacity-60">Success Trend: +15% this week</p>
                        </div>
                        <div className="bg-white rounded-[3rem] p-12 border border-slate-200 shadow-xl flex flex-col justify-center text-center">
                            <h3 className="text-3xl font-bold text-[#011E26] mb-6">Ready to Level Up?</h3>
                            <p className="text-slate-500 text-lg mb-10">
                                Unlock full access to interactive exam simulations, advanced acoustic analytics, and
                                daily speaking challenges.
                            </p>
                            <button
                                onClick={handleUpgrade}
                                className="group w-full max-w-sm mx-auto bg-[#011E26] text-white px-8 py-5 rounded-2xl font-bold text-lg hover:bg-[#009B8F] transition-all flex items-center justify-center gap-3 shadow-lg"
                            >
                                Upgrade to Premium Prep Lab
                                <ArrowRight className="group-hover:translate-x-2 transition-transform" />
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default VoiceCoach;
